import React from "react";
import PropTypes from "prop-types";
import { humanEUR } from "../blocks/format";

// Finance-grade palette (SP1 approved mockup), colour-coded by delta SIGN so a reader can tell
// "this made spend worse" from "this helped" without reading the number: adverse (cost UP,
// delta > 0) is red, favourable (cost DOWN, delta < 0) is green. Start/end bars are the totals
// themselves (not a direction), so they get neutral/accent tones instead.
export const ADVERSE_COLOR = "#b23c2e";
export const FAVOURABLE_COLOR = "#1f7a4d";
export const NEUTRAL_COLOR = "#9aa0a8";
export const START_COLOR = "#6b6f76";
export const END_COLOR = "#2b3a67";
// SP2 P/V/M: a "driver" step's floating bar is split into a Volume sub-rect and a Rate
// sub-rect stacked within it. These two shades are deliberately NOT sign-coloured (unlike
// bucket steps) - Volume/Rate is an orthogonal axis (what MOVED) to adverse/favourable (which
// WAY it moved), so they get their own two-tone finance-grade family instead of borrowing red/
// green, which would collide with the sign meaning used everywhere else on this chart.
export const VOLUME_COLOR = "#2f6f8f";
export const RATE_COLOR = "#8fc1d6";
const AXIS_COLOR = "#6b6b6b";
const CONNECTOR_COLOR = "#b7bac0";

// Bucket step kinds from the /figures/pvm shape - always a single (unsplit) bar. new/dropped/
// one_off are sign-coloured like any other step; other/unexplained are always muted grey
// regardless of sign, since they represent "couldn't attribute" rather than a directional
// finding.
const BUCKET_KINDS = new Set(["new", "dropped", "one_off", "other", "unexplained"]);
const MUTED_BUCKET_KINDS = new Set(["other", "unexplained"]);

// SVG chosen over Recharts: a waterfall is fundamentally "floating bars at an absolute
// (bottom, top) in value-space" - Recharts' stacked-Bar approach needs a transparent "base"
// series plus a visible "delta" series kept in lockstep, which is exactly the running-cumulative
// bookkeeping below wearing a second, harder-to-read hat. Plain inline SVG lets the same
// (bottom, top) pair drive the rect directly, keeps the print-static (no interactivity) goal
// trivial, and matches the approved mockup pixel-for-pixel without fighting a charting library's
// stacking model.
const VIEW_WIDTH = 900;
const VIEW_HEIGHT = 240;
const PLOT_TOP = 28; // room for the value label above the tallest bar
const PLOT_BOTTOM = VIEW_HEIGHT - 44; // room for the category-label row + connector baseline
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP;
const BAR_GAP_FRACTION = 0.32; // fraction of each slot's width left as gap between bars

// signedCompactEUR - "+€18k" / "-€6k": humanEUR already handles the minus sign for negative
// input, so this passes the absolute value through and prepends the sign itself to avoid a
// double negative, and to force an explicit "+" on non-negative deltas (a bare "€18k" reads as
// a total, not a change).
function signedCompactEUR(v) {
  const n = Number(v) || 0;
  return `${n >= 0 ? "+" : "-"}${humanEUR(Math.abs(n))}`;
}

// signColor - the one color-by-sign rule shared by every non-driver, non-muted bar: adverse
// (cost up) red, favourable (cost down) green, exactly-zero neutral grey.
function signColor(delta) {
  return delta > 0 ? ADVERSE_COLOR : delta < 0 ? FAVOURABLE_COLOR : NEUTRAL_COLOR;
}

// splitVolumeRate - pure: how much of a driver bar's total (bottom, top) span belongs to the
// Volume sub-segment vs. the Rate sub-segment. Always sized by RELATIVE MAGNITUDE
// (|volume| / (|volume| + |rate|)), never by each figure's own sign. This is the documented
// simplification for the case where volume and rate move in OPPOSITE directions (e.g. usage up,
// unit price down, so |volume| + |rate| > |delta|): rendering an actual over/undershoot past the
// bar's own (bottom, top) span would break the running-cumulative reconciliation the whole chart
// depends on (each bar must start exactly where the last one left off, and the bar's own span IS
// the reconciled delta) - so both same-sign and opposite-sign drivers share one rule: split the
// bar's already-reconciled height proportionally by magnitude. For same-sign drivers this reads
// exactly as "how much of the move was volume vs. rate"; for opposite-sign drivers it reads as
// "how much of the *net* move each side contributed on a magnitude basis" - a simple, always-
// in-bounds rendering, not a claim that the smaller figure was literally clipped to fit.
export function splitVolumeRate(volume, rate) {
  const v = Math.abs(Number(volume) || 0);
  const r = Math.abs(Number(rate) || 0);
  const total = v + r;
  if (total === 0) return { volumeFraction: 0.5, rateFraction: 0.5 };
  return { volumeFraction: v / total, rateFraction: r / total };
}

// buildWaterfallBars - pure helper: turns either envelope shape into a flat list of floating
// bars, each an absolute (bottom, top) pair in value-space. Start/end bars run from 0 (they ARE
// the totals); step bars float between the running cumulative before and after that step.
//
// Two shapes are accepted (told apart per-step by `kind`, and at the top level by whether
// `residual` is present):
//  - SP1 GET /figures/waterfall: {prev_total, steps:[{label,delta}], residual, current_total}
//    (Task-4 internal/boardfigures/waterfall.go). Every step is a single sign-coloured bar,
//    always rendered (SP1 has no "hide a zero step" rule); `residual` (when present, even 0) is
//    rendered as one more floating bar. This shape is still handled here for the standalone
//    figure/tests, even though the VarianceWaterfallBlock consumer moved to the segmented
//    /figures/pvm shape below in SP2-T6.
//  - SP2 GET /figures/pvm: {prev_total, steps:[{label,kind,delta,volume,rate,category}],
//    current_total} (no top-level `residual` - a `kind:"unexplained"` step plays that role
//    instead). `kind:"driver"` steps are split into Volume/Rate sub-segments (see
//    splitVolumeRate); bucket kinds (new/dropped/one_off/other/unexplained) are single bars,
//    rendered ONLY when their delta is non-zero (one_off is always 0 in v1, other/dropped/new
//    may be 0 too - a zero bucket bar would just be a same-value connector with nothing to show).
//
// Reconciliation (prev_total + Σsteps.delta [+ residual] === current_total) is guaranteed
// upstream by the backend, not re-derived here; skipping a zero-delta bucket step never breaks
// it, because "running" only ever advances by that step's own (zero) delta.
export function buildWaterfallBars(data) {
  if (!data) return [];
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const prevTotal = Number(data.prev_total) || 0;
  const currentTotal = Number(data.current_total) || 0;
  const hasResidual = data.residual !== undefined && data.residual !== null;
  const residual = Number(data.residual) || 0;

  const bars = [
    {
      key: "start",
      role: "start",
      label: data.prev_label || "Previous",
      valueLabel: humanEUR(prevTotal),
      bottom: 0,
      top: prevTotal,
      after: prevTotal, // cumulative value carried into the next bar
      color: START_COLOR,
    },
  ];

  let running = prevTotal;
  // floatingSpan - advances the running cumulative by `delta` and returns the bar's absolute
  // (bottom, top): a positive delta floats UP from the prior cumulative, a negative delta
  // floats DOWN below it (never swaps branches and renders from 0).
  const floatingSpan = (delta) => {
    const bottom = delta >= 0 ? running : running + delta;
    const top = delta >= 0 ? running + delta : running;
    running += delta;
    return { bottom, top };
  };

  steps.forEach((step, i) => {
    const kind = step?.kind;
    const delta = Number(step?.delta) || 0;
    const label = step?.label || `Step ${i + 1}`;

    if (kind === "driver") {
      const { bottom, top } = floatingSpan(delta);
      const { volumeFraction } = splitVolumeRate(step?.volume, step?.rate);
      const volumeTop = bottom + (top - bottom) * volumeFraction;
      bars.push({
        key: `step-${i}`,
        role: "driver",
        label,
        valueLabel: signedCompactEUR(delta),
        bottom,
        top,
        after: running,
        segments: [
          { type: "volume", bottom, top: volumeTop, color: VOLUME_COLOR },
          { type: "rate", bottom: volumeTop, top, color: RATE_COLOR },
        ],
      });
      return;
    }

    if (BUCKET_KINDS.has(kind)) {
      if (delta === 0) return; // v1: one_off is always 0; other/dropped/new may be too - skip.
      const { bottom, top } = floatingSpan(delta);
      bars.push({
        key: `step-${i}`,
        role: "bucket",
        kind,
        label,
        valueLabel: signedCompactEUR(delta),
        bottom,
        top,
        after: running,
        color: MUTED_BUCKET_KINDS.has(kind) ? NEUTRAL_COLOR : signColor(delta),
      });
      return;
    }

    // Legacy SP1 shape: a plain {label, delta} step with no `kind`.
    const { bottom, top } = floatingSpan(delta);
    bars.push({
      key: `step-${i}`,
      role: "step",
      label,
      valueLabel: signedCompactEUR(delta),
      bottom,
      top,
      after: running,
      color: signColor(delta),
    });
  });

  // Residual reconciles the named (top-N) drivers to the true month-over-month delta (SP1
  // shape only - pvm's equivalent role is the `unexplained` bucket step above). Rendered as
  // just another floating bar, coloured by its own sign, only when the field is present at all
  // (so pvm data, which never sets it, gets no phantom "Other / unattributed" bar).
  if (hasResidual) {
    const { bottom, top } = floatingSpan(residual);
    bars.push({
      key: "residual",
      role: "residual",
      label: "Other / unattributed",
      valueLabel: signedCompactEUR(residual),
      bottom,
      top,
      after: running,
      color: signColor(residual),
    });
  }

  bars.push({
    key: "end",
    role: "end",
    label: data.current_label || "Current",
    valueLabel: humanEUR(currentTotal),
    bottom: 0,
    top: currentTotal,
    after: currentTotal,
    color: END_COLOR,
  });

  return bars;
}

const LEGEND_ITEMS = [
  { key: "increase", label: "Increase", color: ADVERSE_COLOR },
  { key: "decrease", label: "Decrease", color: FAVOURABLE_COLOR },
  { key: "volume", label: "Volume", color: VOLUME_COLOR },
  { key: "rate", label: "Rate", color: RATE_COLOR },
];

// StaticLegend - plain HTML swatches, not an interactive chart-library legend (mirrors
// StackedTrendChart/ForecastBandChart's own StaticLegend - see those files for the fuller
// rationale: this chart only ever renders once into a headless-Chrome PDF, so a click-to-toggle
// control would be dead chrome). SP2 adds Volume/Rate to explain the new driver sub-segments;
// Increase/Decrease documents the sign-colour convention that was previously only implicit.
function StaticLegend() {
  return (
    <div
      className="variance-waterfall-chart-legend"
      role="list"
      aria-label="Chart legend"
      style={{
        display: "flex", gap: "16px", justifyContent: "center", marginTop: "6px",
        fontSize: "9pt", color: AXIS_COLOR, fontFamily: "Georgia, 'Times New Roman', serif",
      }}
    >
      {LEGEND_ITEMS.map((item) => (
        <span key={item.key} role="listitem" style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <span
            aria-hidden="true"
            style={{ width: "10px", height: "10px", borderRadius: "2px", background: item.color, display: "inline-block" }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * VarianceWaterfallChart - print-static MoM variance waterfall for the board report.
 *
 * Accepts two shapes (see `buildWaterfallBars` for the full contract):
 *  - SP1 Task-4 GET /figures/waterfall -> { waterfall: { prev_label, prev_total, steps:
 *    [{label, delta}], residual, current_label, current_total } }.
 *  - SP2 GET /figures/pvm -> { prev_label, prev_total, steps: [{label, kind, delta, volume,
 *    rate, category}], current_label, current_total, drivers }. `kind:"driver"` steps render
 *    as a split Volume/Rate bar; bucket kinds render as a single bar, hidden entirely when
 *    their delta is 0.
 *
 * Print-static: no Tooltip, no hover/zoom, no click handlers - renders once into a
 * headless-Chrome PDF. Every bar's value and category name is drawn as static SVG <text> so the
 * figure is fully legible without any interaction.
 */
export default function VarianceWaterfallChart({ data }) {
  const bars = buildWaterfallBars(data);

  if (bars.length === 0) {
    return (
      <div
        className="variance-waterfall-chart-empty"
        style={{ fontSize: "9pt", color: AXIS_COLOR, fontStyle: "italic", padding: "8px 0" }}
      >
        No variance data available.
      </div>
    );
  }

  const allValues = bars.flatMap((b) => [b.bottom, b.top]);
  const maxVal = Math.max(0, ...allValues);
  const minVal = Math.min(0, ...allValues);
  const range = maxVal - minVal || 1;
  const yFor = (v) => PLOT_TOP + PLOT_HEIGHT * (1 - (v - minVal) / range);

  const slotWidth = VIEW_WIDTH / bars.length;
  const barWidth = slotWidth * (1 - BAR_GAP_FRACTION);
  const baselineY = yFor(0);

  const positioned = bars.map((bar, i) => {
    const xCenter = slotWidth * i + slotWidth / 2;
    const yTop = yFor(bar.top);
    const yBottom = yFor(bar.bottom);
    // Driver bars carry `segments` (Volume/Rate sub-rects) - map each sub-segment's own
    // (bottom, top) through the same yFor scale used for the outer bar, so the two stacked
    // rects always fill the outer bar's pixel span exactly (no rounding gap/overlap).
    const segments = Array.isArray(bar.segments)
      ? bar.segments.map((seg) => {
          const segYTop = yFor(seg.top);
          const segYBottom = yFor(seg.bottom);
          return {
            ...seg,
            y: Math.min(segYTop, segYBottom),
            height: Math.max(0, Math.abs(segYBottom - segYTop)),
          };
        })
      : null;
    return {
      ...bar,
      x: xCenter - barWidth / 2,
      xCenter,
      y: Math.min(yTop, yBottom),
      height: Math.max(1, Math.abs(yBottom - yTop)),
      segments,
    };
  });

  return (
    <div className="variance-waterfall-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height: VIEW_HEIGHT }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Month-over-month variance waterfall"
        >
          <line
            x1={0}
            y1={baselineY}
            x2={VIEW_WIDTH}
            y2={baselineY}
            stroke={AXIS_COLOR}
            strokeWidth={1}
          />
          {/* Connector lines between consecutive bars - a horizontal dashed thread at the
              running-cumulative value carried from one bar into the next, so a reader can trace
              the walk from prev_total to current_total without doing the arithmetic themselves. */}
          {positioned.slice(0, -1).map((bar, i) => {
            const next = positioned[i + 1];
            const y = yFor(bar.after);
            return (
              <line
                key={`connector-${bar.key}`}
                x1={bar.xCenter + barWidth / 2}
                y1={y}
                x2={next.xCenter - barWidth / 2}
                y2={y}
                stroke={CONNECTOR_COLOR}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            );
          })}
          {positioned.map((bar) => (
            <g key={bar.key}>
              {bar.segments ? (
                // Driver step: two stacked sub-rects (Volume, Rate) filling the bar's exact
                // pixel span - no separate "outer" bar rect underneath.
                bar.segments.map((seg) => (
                  <rect
                    key={`${bar.key}-${seg.type}`}
                    className="waterfall-bar"
                    data-role={bar.role}
                    data-segment={seg.type}
                    data-step-label={bar.label}
                    x={bar.x}
                    y={seg.y}
                    width={barWidth}
                    height={seg.height}
                    fill={seg.color}
                  />
                ))
              ) : (
                <rect
                  className="waterfall-bar"
                  data-role={bar.role}
                  data-kind={bar.kind}
                  data-step-label={
                    bar.role === "step" || bar.role === "bucket" ? bar.label : undefined
                  }
                  x={bar.x}
                  y={bar.y}
                  width={barWidth}
                  height={bar.height}
                  fill={bar.color}
                />
              )}
              <text
                x={bar.xCenter}
                y={bar.y - 8}
                textAnchor="middle"
                fontSize="11"
                fontFamily="Georgia, 'Times New Roman', serif"
                fill={AXIS_COLOR}
              >
                {bar.valueLabel}
              </text>
              <text
                x={bar.xCenter}
                y={PLOT_BOTTOM + 20}
                textAnchor="middle"
                fontSize="10"
                fontFamily="Georgia, 'Times New Roman', serif"
                fill={AXIS_COLOR}
              >
                {bar.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <StaticLegend />
    </div>
  );
}

VarianceWaterfallChart.propTypes = {
  data: PropTypes.shape({
    prev_label: PropTypes.string,
    prev_total: PropTypes.number,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        delta: PropTypes.number,
        kind: PropTypes.oneOf(["driver", "new", "dropped", "one_off", "other", "unexplained"]),
        volume: PropTypes.number,
        rate: PropTypes.number,
        category: PropTypes.string,
      })
    ),
    residual: PropTypes.number,
    current_label: PropTypes.string,
    current_total: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types -- pvm's `drivers` array is passed
    // through untouched (not rendered by this chart); shape intentionally left open.
    drivers: PropTypes.array,
  }),
};
